import { useCallback, useRef, useState } from 'react'

import type { ExtendedPlanSelectorData } from '../utils/dataTransformers'

interface PlanOverrideDialogProps {
  subscription?: ExtendedPlanSelectorData['subscription']
  onConfirm: () => Promise<void>
  loading: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface UsePlanOverrideDialogReturn {
  showOverrideConfirmation: (
    subscription: ExtendedPlanSelectorData['subscription'],
  ) => Promise<boolean>
  dialogProps: PlanOverrideDialogProps
}

/**
 * Custom hook for managing a promise-based plan override confirmation dialog.
 *
 * This hook provides a clean async interface for showing a dialog and waiting
 * for user confirmation, while managing all the necessary state internally.
 *
 * @param loading - External loading state to show in the dialog
 * @returns Object containing showOverrideConfirmation function and dialog props
 */
export const usePlanOverrideDialog = (
  loading: boolean = false,
): UsePlanOverrideDialogReturn => {
  const [showDialog, setShowDialog] = useState(false)
  const [subscription, setSubscription] = useState<
    ExtendedPlanSelectorData['subscription'] | null
  >(null)
  const confirmResolveRef = useRef<((confirmed: boolean) => void) | null>(null)

  const showOverrideConfirmation = useCallback(
    async (
      subscription: ExtendedPlanSelectorData['subscription'],
    ): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        confirmResolveRef.current = resolve
        setSubscription(subscription)
        setShowDialog(true)
      })
    },
    [],
  )

  const handleConfirm = useCallback((): Promise<void> => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(true)
      confirmResolveRef.current = null
    }
    setShowDialog(false)
    setSubscription(null)
    return Promise.resolve()
  }, [])

  const handleCancel = useCallback((): void => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false)
      confirmResolveRef.current = null
    }
    setShowDialog(false)
    setSubscription(null)
  }, [])

  const dialogProps: PlanOverrideDialogProps = {
    subscription: subscription ?? undefined,
    onConfirm: handleConfirm,
    loading,
    open: showDialog,
    onOpenChange: (open) => {
      if (!open) {
        handleCancel()
      }
    },
  }

  return {
    showOverrideConfirmation,
    dialogProps,
  }
}
